/**
 * Why You'll Love It — scoped section interactions
 * Supports multiple instances via [data-why-youll-love-it]
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 1024px)';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function WhyYoullLoveIt(root) {
    this.root = root;
    this.index = 0;
    this.desktopMq = window.matchMedia(DESKTOP_MQ);
    this.scrollTimer = null;
    this.isProgrammaticScroll = false;

    this.navItems = Array.prototype.slice.call(root.querySelectorAll('[data-wyli-nav-item]'));
    this.desktopMedia = Array.prototype.slice.call(
      root.querySelectorAll('[data-wyli-desktop-stage] [data-wyli-media]')
    );
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-wyli-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-wyli-dot]'));
    this.track = root.querySelector('[data-wyli-track]');

    this.onNavEnter = this.onNavEnter.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onNavKeydown = this.onNavKeydown.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMqChange = this.onMqChange.bind(this);

    this.bind();
    this.labelVideos();
    this.setActive(0, { syncScroll: true, instant: true });
  }

  WhyYoullLoveIt.prototype.bind = function () {
    var self = this;

    this.navItems.forEach(function (btn) {
      btn.addEventListener('mouseenter', self.onNavEnter);
      btn.addEventListener('focus', self.onNavEnter);
      btn.addEventListener('click', self.onNavClick);
      btn.addEventListener('keydown', self.onNavKeydown);
    });

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', self.onDotClick);
    });

    if (this.track) {
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
    }

    window.addEventListener('resize', this.onResize);

    if (this.desktopMq.addEventListener) {
      this.desktopMq.addEventListener('change', this.onMqChange);
    } else if (this.desktopMq.addListener) {
      this.desktopMq.addListener(this.onMqChange);
    }
  };

  WhyYoullLoveIt.prototype.labelVideos = function () {
    var self = this;
    var titleByIndex = {};

    this.navItems.forEach(function (btn) {
      var idx = parseInt(btn.getAttribute('data-wyli-index'), 10);
      var label = btn.textContent ? btn.textContent.trim() : '';
      if (!isNaN(idx) && label) titleByIndex[idx] = label;
    });

    this.root.querySelectorAll('[data-wyli-media][data-media-kind="video"]').forEach(function (wrap) {
      var idx = parseInt(wrap.getAttribute('data-wyli-index'), 10);
      var video = wrap.querySelector('video');
      if (!video) return;
      var label = wrap.getAttribute('data-title') || titleByIndex[idx] || '';
      if (label) video.setAttribute('aria-label', label);
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.loop = true;
    });
  };

  WhyYoullLoveIt.prototype.isDesktop = function () {
    return this.desktopMq.matches;
  };

  WhyYoullLoveIt.prototype.onNavEnter = function (event) {
    if (!this.isDesktop()) return;
    var idx = parseInt(event.currentTarget.getAttribute('data-wyli-index'), 10);
    if (isNaN(idx) || idx === this.index) return;
    this.setActive(idx);
  };

  WhyYoullLoveIt.prototype.onNavClick = function (event) {
    var idx = parseInt(event.currentTarget.getAttribute('data-wyli-index'), 10);
    if (isNaN(idx)) return;
    this.setActive(idx);
  };

  WhyYoullLoveIt.prototype.onNavKeydown = function (event) {
    var key = event.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') return;

    event.preventDefault();
    var next = this.index;
    if (key === 'ArrowDown') next = Math.min(this.index + 1, this.navItems.length - 1);
    if (key === 'ArrowUp') next = Math.max(this.index - 1, 0);
    if (key === 'Home') next = 0;
    if (key === 'End') next = this.navItems.length - 1;

    this.setActive(next);
    if (this.navItems[next]) this.navItems[next].focus();
  };

  WhyYoullLoveIt.prototype.onDotClick = function (event) {
    var idx = parseInt(event.currentTarget.getAttribute('data-wyli-index'), 10);
    if (isNaN(idx)) return;
    this.setActive(idx, { syncScroll: true });
  };

  WhyYoullLoveIt.prototype.onScroll = function () {
    var self = this;
    if (this.isDesktop() || this.isProgrammaticScroll) return;

    if (this._scrollRaf) return;
    this._scrollRaf = window.requestAnimationFrame(function () {
      self._scrollRaf = null;
      var idx = self.getIndexFromScroll();
      if (idx === self.index) return;

      self.index = idx;
      self.updateDots(idx);
      self.updateSlides(idx);

      window.clearTimeout(self.scrollTimer);
      self.scrollTimer = window.setTimeout(function () {
        self.syncVideos();
      }, 80);
    });
  };

  WhyYoullLoveIt.prototype.updateDots = function (index) {
    this.dots.forEach(function (dot, idx) {
      var active = idx === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  };

  WhyYoullLoveIt.prototype.updateSlides = function (index) {
    this.slides.forEach(function (slide, idx) {
      slide.classList.toggle('is-active', idx === index);
    });
  };

  WhyYoullLoveIt.prototype.onResize = function () {
    var self = this;
    window.clearTimeout(this._resizeTimer);
    this._resizeTimer = window.setTimeout(function () {
      if (!self.isDesktop()) {
        self.scrollToIndex(self.index, true);
      }
      self.syncVideos();
    }, 100);
  };

  WhyYoullLoveIt.prototype.onMqChange = function () {
    this.setActive(this.index, { syncScroll: true, instant: true });
  };

  WhyYoullLoveIt.prototype.getIndexFromScroll = function () {
    if (!this.track || !this.slides.length) return 0;

    var maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    var scrollLeft = this.track.scrollLeft;

    // With peek slides the last card never fully left-aligns; pin last index at end.
    if (maxScroll > 0 && scrollLeft >= maxScroll - 4) {
      return this.slides.length - 1;
    }

    var first = this.slides[0];
    var stride = first.offsetWidth;
    if (this.slides.length > 1) {
      stride = this.slides[1].offsetLeft - first.offsetLeft;
    }
    if (stride <= 0) return 0;

    var idx = Math.round(scrollLeft / stride);
    if (idx < 0) idx = 0;
    if (idx > this.slides.length - 1) idx = this.slides.length - 1;
    return idx;
  };

  WhyYoullLoveIt.prototype.scrollToIndex = function (index, instant) {
    var slide = this.slides[index];
    if (!this.track || !slide) return;

    var maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    var left = slide.offsetLeft;
    if (index >= this.slides.length - 1) {
      left = maxScroll;
    } else {
      left = Math.min(left, maxScroll);
    }

    this.isProgrammaticScroll = true;

    if (instant || prefersReducedMotion()) {
      this.track.scrollLeft = left;
    } else if (typeof this.track.scrollTo === 'function') {
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = left;
    }

    var self = this;
    window.setTimeout(function () {
      self.isProgrammaticScroll = false;
      // Re-sync after programmatic scroll settles (snap / max-scroll clamping).
      var settled = self.getIndexFromScroll();
      if (settled !== self.index) {
        self.index = settled;
        self.updateDots(settled);
        self.updateSlides(settled);
      }
    }, instant ? 50 : 400);
  };

  WhyYoullLoveIt.prototype.pauseVideosIn = function (container) {
    if (!container) return;
    container.querySelectorAll('video').forEach(function (video) {
      try {
        video.pause();
      } catch (e) {}
    });
  };

  WhyYoullLoveIt.prototype.playVideoIn = function (container) {
    if (!container) return;
    var video = container.querySelector('video');
    if (!video) return;
    video.muted = true;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
  };

  WhyYoullLoveIt.prototype.syncVideos = function () {
    var self = this;
    var activeIndex = this.index;

    if (this.isDesktop()) {
      this.slides.forEach(function (slide) {
        self.pauseVideosIn(slide);
      });
      this.desktopMedia.forEach(function (item, idx) {
        if (idx === activeIndex) {
          self.playVideoIn(item);
        } else {
          self.pauseVideosIn(item);
        }
      });
    } else {
      this.desktopMedia.forEach(function (item) {
        self.pauseVideosIn(item);
      });
      this.slides.forEach(function (slide, idx) {
        if (idx === activeIndex) {
          self.playVideoIn(slide);
        } else {
          self.pauseVideosIn(slide);
        }
      });
    }
  };

  WhyYoullLoveIt.prototype.setActive = function (index, options) {
    options = options || {};
    if (index < 0) index = 0;

    var max = Math.max(
      this.navItems.length,
      this.desktopMedia.length,
      this.slides.length,
      this.dots.length
    ) - 1;
    if (max < 0) return;
    if (index > max) index = max;

    this.index = index;

    this.navItems.forEach(function (btn, idx) {
      var active = idx === index;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      btn.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.desktopMedia.forEach(function (item, idx) {
      var active = idx === index;
      item.classList.toggle('is-active', active);
      if (active) {
        item.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', '');
      }
    });

    this.slides.forEach(function (slide, idx) {
      slide.classList.toggle('is-active', idx === index);
    });

    this.dots.forEach(function (dot, idx) {
      var active = idx === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (options.syncScroll && !this.isDesktop()) {
      this.scrollToIndex(index, !!options.instant);
    }

    if (!options.skipVideos) {
      this.syncVideos();
    }
  };

  function initAll() {
    document.querySelectorAll('[data-why-youll-love-it]').forEach(function (root) {
      if (root.__wyliInit) return;
      root.__wyliInit = true;
      new WhyYoullLoveIt(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector
      ? event.target.querySelector('[data-why-youll-love-it]')
      : null;
    if (root && !root.__wyliInit) {
      root.__wyliInit = true;
      new WhyYoullLoveIt(root);
    }
  });
})();
